import {
  Check,
  ChevronDown,
  Copy,
  Edit3,
  Mail,
  Plus,
  ShieldCheck,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../../api/client";
import LoadingState from "../../../components/common/LoadingState";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { useToast } from "../../../context/ToastContext";
import type {
  AdminInvite,
  AdminPermission,
  AdminRole,
  AdminRoleDefinition,
  AdminTeamMember,
  EventItem,
} from "../../../types/api";
import { formatDateTime } from "../../../utils/format";

const PERMISSION_LABELS: Record<AdminPermission, string> = {
  "events:manage": "Manage screenings",
  "events:approve": "Legacy screening approvals",
  "stats:view": "View screening stats",
  "orders:view": "View orders",
  "tickets:scan": "Scan tickets",
  "tickets:issue": "Issue tickets in person",
  "users:view": "View users",
  "catalog:manage": "Manage venues & categories",
  "site:manage": "Manage public site content",
  "team:manage": "Manage team & roles",
  "organizers:review": "Review exhibitor applications",
};

const ALL_PERMISSIONS: AdminPermission[] = [
  "events:manage",
  "stats:view",
  "orders:view",
  "tickets:scan",
  "tickets:issue",
  "users:view",
  "catalog:manage",
  "site:manage",
  "team:manage",
  "organizers:review",
];

const STATUS_BADGE: Record<AdminInvite["status"], string> = {
  pending: "ego-badge ego-badge-warning",
  accepted: "ego-badge ego-badge-success",
  revoked: "ego-badge ego-badge-neutral",
  expired: "ego-badge ego-badge-danger",
};

const ORGANIZER_COLLABORATOR_ROLES = new Set(["scanner", "producer", "content-owner"]);

function roleAccessLabel(role: string) {
  if (role === "scanner") return "Scanner access";
  if (role === "producer") return "Producer stats";
  if (role === "content-owner") return "Content owner stats";
  return "Admin access";
}

function eventScopeSummary(scopes: Array<{ eventId: string }>, events: EventItem[]) {
  if (!scopes.length) return "";
  const eventNames = new Map(events.map((event) => [event.id, event.title]));
  const names = scopes.map((scope) => eventNames.get(scope.eventId) ?? "Assigned screening");
  return names.length > 3 ? `${names.slice(0, 3).join(", ")} +${names.length - 3}` : names.join(", ");
}

function buildInviteUrl(token: string) {
  return `${window.location.origin}/admin/invite/${token}`;
}

function initials(name?: string) {
  if (!name) return "?";
  return (
    name
      .split(" ")
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

export default function AdminTeamPage() {
  const { token, admin } = useAdminAuth();
  const { showToast } = useToast();
  const [members, setMembers] = useState<AdminTeamMember[]>([]);
  const [invites, setInvites] = useState<AdminInvite[]>([]);
  const [roles, setRoles] = useState<AdminRoleDefinition[]>([]);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isRoleEditorOpen, setIsRoleEditorOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<AdminRoleDefinition | null>(null);
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null);
  const isOrganizer = admin?.role === "organizer";

  const roleLabel = useMemo(() => {
    const map: Record<string, string> = {};
    roles.forEach((role) => {
      map[role.name] = role.label;
    });
    return map;
  }, [roles]);

  const load = async () => {
    if (!token) return;
    try {
      const [teamResponse, invitesResponse, rolesResponse] = await Promise.all([
        adminApi.listTeam(token),
        adminApi.listInvites(token),
        adminApi.listRoles(token),
      ]);
      setMembers(teamResponse.admins);
      setInvites(invitesResponse.invites);
      setRoles(rolesResponse.roles);
      const canLoadEvents = admin?.effectivePermissions?.some((permission) =>
        ["events:manage", "stats:view", "tickets:scan"].includes(permission)
      );
      if (canLoadEvents) {
        const eventsResponse = await adminApi.listEvents(token);
        setEvents(eventsResponse.events);
      }
    } catch (requestError) {
      showToast(
        requestError instanceof Error ? requestError.message : "Could not load team",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const refreshRoles = async () => {
    if (!token) return;
    const { roles: next } = await adminApi.listRoles(token);
    setRoles(next);
  };

  const copyInviteLink = async (linkToken: string, inviteId: string) => {
    try {
      await navigator.clipboard.writeText(buildInviteUrl(linkToken));
      setCopiedInviteId(inviteId);
      window.setTimeout(() => setCopiedInviteId(null), 1500);
      showToast("Invite link copied", "success");
    } catch {
      showToast("Could not copy. Please copy manually.", "error");
    }
  };

  const handleRoleChange = async (memberId: string, role: AdminRole) => {
    if (!token) return;
    try {
      const { admin: updated } = await adminApi.updateTeamMember(token, memberId, { role });
      setMembers((current) =>
        current.map((entry) => (entry.id === memberId ? updated : entry))
      );
      showToast("Role updated", "success");
    } catch (requestError) {
      showToast(
        requestError instanceof Error ? requestError.message : "Could not update role",
        "error"
      );
    }
  };

  const handlePermissionToggle = async (
    member: AdminTeamMember,
    permission: AdminPermission
  ) => {
    if (!token) return;
    const next = member.permissions.includes(permission)
      ? member.permissions.filter((entry) => entry !== permission)
      : [...member.permissions, permission];
    try {
      const { admin: updated } = await adminApi.updateTeamMember(token, member.id, {
        permissions: next,
      });
      setMembers((current) =>
        current.map((entry) => (entry.id === member.id ? updated : entry))
      );
    } catch (requestError) {
      showToast(
        requestError instanceof Error ? requestError.message : "Could not update permissions",
        "error"
      );
    }
  };

  const handleRemoveMember = async (member: AdminTeamMember) => {
    if (!token) return;
    if (!window.confirm(`Remove ${member.name}? They'll lose admin access.`)) return;
    try {
      await adminApi.removeTeamMember(token, member.id);
      setMembers((current) => current.filter((entry) => entry.id !== member.id));
      showToast(`${member.name} removed`, "success");
    } catch (requestError) {
      showToast(
        requestError instanceof Error ? requestError.message : "Could not remove admin",
        "error"
      );
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    if (!token) return;
    try {
      const { invite } = await adminApi.revokeInvite(token, inviteId);
      setInvites((current) =>
        current.map((entry) => (entry.id === invite.id ? invite : entry))
      );
      showToast("Invite revoked", "success");
    } catch (requestError) {
      showToast(
        requestError instanceof Error ? requestError.message : "Could not revoke invite",
        "error"
      );
    }
  };

  if (isLoading) return <LoadingState label="Loading team..." />;

  return (
    <div className="space-y-6">
      <div className="ego-surface flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Team & permissions</h2>
          <p className="text-sm text-muted-foreground">
            {isOrganizer
              ? "Invite gate scanners and content collaborators for your screenings."
              : "Define roles, invite collaborators, and control what each admin can access."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isOrganizer && (
            <button
              type="button"
              onClick={() => {
                setEditingRole(null);
                setIsRoleEditorOpen(true);
              }}
              className="ego-btn-secondary"
            >
              <Plus size={15} /> New role
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setLastInviteUrl(null);
              setIsInviteOpen(true);
            }}
            className="ego-btn-primary"
          >
            <UserPlus size={15} /> {isOrganizer ? "Invite collaborator" : "Invite admin"}
          </button>
        </div>
      </div>

      {!isOrganizer && (
        <RolesSection
          roles={roles}
          onEdit={(role) => {
            setEditingRole(role);
            setIsRoleEditorOpen(true);
          }}
          onDelete={async (role) => {
            if (!token) return;
            if (!window.confirm(`Delete the "${role.label}" role?`)) return;
            try {
              await adminApi.deleteRole(token, role.id);
              await refreshRoles();
              showToast(`Deleted ${role.label}`, "success");
            } catch (requestError) {
              showToast(
                requestError instanceof Error ? requestError.message : "Could not delete role",
                "error"
              );
            }
          }}
        />
      )}

      {invites.filter((invite) => invite.status === "pending").length > 0 && (
        <div className="ego-surface overflow-hidden">
          <div className="border-b border-line px-6 py-4">
            <h3 className="text-sm font-bold text-foreground">Pending invites</h3>
          </div>
          <div className="ego-divide">
            {invites.map((invite) => (
              <div
                key={invite.id}
                className="grid gap-3 px-6 py-4 lg:grid-cols-[1.4fr_0.8fr_1fr_auto] lg:items-center"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-foreground">{invite.email}</div>
                  <div className="text-xs text-muted-foreground">
                    Invited by {invite.invitedByEmail || "system"} ·{" "}
                    {formatDateTime(invite.expiresAt)}
                  </div>
                </div>
                <div>
                  <span className={STATUS_BADGE[invite.status]}>{invite.status}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {roleLabel[invite.role] ?? invite.role}
                  {invite.permissions.length > 0 && (
                    <span> · {invite.permissions.length} extra perms</span>
                  )}
                  {eventScopeSummary(invite.eventScopes, events) && (
                    <span> · {eventScopeSummary(invite.eventScopes, events)}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {invite.status === "pending" && (
                    <>
                      <button
                        type="button"
                        onClick={() => copyInviteLink(invite.token ?? invite.id, invite.id)}
                        className="ego-btn-secondary !py-1.5 !px-3 text-xs"
                      >
                        {copiedInviteId === invite.id ? <Check size={13} /> : <Copy size={13} />}
                        {copiedInviteId === invite.id ? "Copied" : "Copy link"}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRevokeInvite(invite.id)}
                        className="ego-btn-danger !py-1.5 !px-3 text-xs"
                      >
                        Revoke
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="ego-surface overflow-hidden">
        <div className="border-b border-line px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">Admins</h3>
          <p className="text-xs text-muted-foreground">{members.length} active</p>
        </div>
        <div className="ego-divide">
          {members.map((member) => {
            const isSelf = admin?.id === member.id;
            return (
              <div key={member.id} className="space-y-3 px-6 py-5">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0b1220] text-xs font-bold text-white">
                    {initials(member.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="truncate text-sm font-semibold text-foreground">
                        {member.name}
                      </div>
                      {isSelf && <span className="ego-badge ego-badge-brand">You</span>}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">{member.email}</div>
                  </div>

                  {isOrganizer ? (
                    <span className="ego-badge ego-badge-neutral">{roleAccessLabel(member.role)}</span>
                  ) : (
                    <select
                      value={member.role}
                      onChange={(event) => handleRoleChange(member.id, event.target.value)}
                      className="ego-input !w-auto !py-1.5 text-xs"
                      disabled={isSelf}
                    >
                      {roles.map((role) => (
                        <option key={role.id} value={role.name}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  )}

                  {!isSelf && (
                    <button
                      type="button"
                      onClick={() => handleRemoveMember(member)}
                      className="ego-btn-danger !py-1.5 !px-2.5"
                      aria-label="Remove admin"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>

                {isOrganizer && member.eventScopes.length > 0 && (
                  <div className="rounded-xl border border-line bg-surface-muted px-4 py-3 text-xs text-muted-foreground">
                    Assigned to{" "}
                    <span className="font-semibold text-foreground">
                      {eventScopeSummary(member.eventScopes, events)}
                    </span>
                  </div>
                )}

                {!isOrganizer && member.role !== "super-admin" && (
                  <div>
                    <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <ShieldCheck size={12} /> Extra permissions
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {ALL_PERMISSIONS.map((permission) => {
                        const isExplicit = member.permissions.includes(permission);
                        const isEffective = member.effectivePermissions.includes(permission);
                        const isInherited = isEffective && !isExplicit;
                        return (
                          <button
                            key={permission}
                            type="button"
                            onClick={() => handlePermissionToggle(member, permission)}
                            disabled={isInherited}
                            className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${
                              isExplicit
                                ? "border-[var(--pp-gold)] bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]"
                                : isInherited
                                  ? "border-line bg-surface-muted text-muted-foreground"
                                  : "border-line bg-surface text-muted-foreground hover:bg-surface-muted"
                            }`}
                            title={isInherited ? "Granted by role" : ""}
                          >
                            {PERMISSION_LABELS[permission]}
                            {isInherited && " (role)"}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {member.role === "super-admin" && (
                  <div className="text-[11px] text-[#9aa3b2]">
                    Super admins have all permissions automatically.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {isInviteOpen && (
        <InviteAdminDialog
          roles={roles}
          events={events}
          isOrganizer={isOrganizer}
          existingInviteUrl={lastInviteUrl}
          onClose={() => setIsInviteOpen(false)}
          onCreated={(invite, inviteToken) => {
            setInvites((current) => [invite, ...current]);
            setLastInviteUrl(buildInviteUrl(inviteToken));
          }}
        />
      )}

      {isRoleEditorOpen && (
        <RoleEditorDialog
          role={editingRole}
          onClose={() => setIsRoleEditorOpen(false)}
          onSaved={async () => {
            await refreshRoles();
          }}
        />
      )}
    </div>
  );
}

function RolesSection({
  roles,
  onEdit,
  onDelete,
}: {
  roles: AdminRoleDefinition[];
  onEdit: (role: AdminRoleDefinition) => void;
  onDelete: (role: AdminRoleDefinition) => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="ego-surface overflow-hidden">
      <div className="border-b border-line px-6 py-4">
        <h3 className="text-sm font-bold text-foreground">Roles</h3>
        <p className="text-xs text-muted-foreground">{roles.length} configured · click to expand</p>
      </div>
      <div className="ego-divide">
        {roles.map((role) => {
          const isExpanded = expandedId === role.id;
          return (
            <div key={role.id} className="px-6 py-4">
              <button
                type="button"
                onClick={() => setExpandedId(isExpanded ? null : role.id)}
                className="flex w-full items-center justify-between gap-3 text-left"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-sm font-semibold text-foreground">{role.label}</div>
                    {role.isSystem && (
                      <span className="ego-badge ego-badge-neutral">System</span>
                    )}
                    <span className="text-xs text-[#9aa3b2]">
                      {role.permissions.length} permission
                      {role.permissions.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  {role.description && (
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">{role.description}</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={(buttonEvent) => {
                      buttonEvent.stopPropagation();
                      onEdit(role);
                    }}
                    className="ego-btn-secondary !py-1.5 !px-2.5"
                    aria-label="Edit role"
                  >
                    <Edit3 size={13} />
                  </button>
                  {!role.isSystem && (
                    <button
                      type="button"
                      onClick={(buttonEvent) => {
                        buttonEvent.stopPropagation();
                        onDelete(role);
                      }}
                      className="ego-btn-danger !py-1.5 !px-2.5"
                      aria-label="Delete role"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                  <ChevronDown
                    size={16}
                    className={`text-[#9aa3b2] transition ${isExpanded ? "rotate-180" : ""}`}
                  />
                </div>
              </button>

              {isExpanded && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {ALL_PERMISSIONS.map((permission) => {
                    const isOn = role.permissions.includes(permission);
                    return (
                      <span
                        key={permission}
                        className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                          isOn
                            ? "border-[var(--pp-gold)] bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]"
                            : "border-line bg-surface-muted text-[#9aa3b2]"
                        }`}
                      >
                        {PERMISSION_LABELS[permission]}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RoleEditorDialog({
  role,
  onClose,
  onSaved,
}: {
  role: AdminRoleDefinition | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { token } = useAdminAuth();
  const { showToast } = useToast();
  const [label, setLabel] = useState(role?.label ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [permissions, setPermissions] = useState<AdminPermission[]>(role?.permissions ?? []);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const togglePermission = (permission: AdminPermission) => {
    setPermissions((current) =>
      current.includes(permission)
        ? current.filter((entry) => entry !== permission)
        : [...current, permission]
    );
  };

  const handleSave = async () => {
    if (!token) return;
    if (!label.trim()) {
      showToast("Role name is required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      if (role) {
        await adminApi.updateRole(token, role.id, {
          label: label.trim(),
          description: description.trim(),
          permissions,
        });
        showToast(`${label.trim()} updated`, "success");
      } else {
        await adminApi.createRole(token, {
          label: label.trim(),
          description: description.trim(),
          permissions,
        });
        showToast(`${label.trim()} created`, "success");
      }
      await onSaved();
      onClose();
    } catch (requestError) {
      showToast(
        requestError instanceof Error ? requestError.message : "Could not save role",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch bg-black/60 p-3 backdrop-blur-md dark:bg-black/75 sm:p-6">
      <div className="mx-auto flex w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-surface shadow-2xl">
        <div className="flex items-start justify-between border-b border-line px-6 py-5">
          <div>
            <span className="ego-badge ego-badge-brand">Role</span>
            <h2 className="mt-2 text-xl font-bold tracking-tight text-foreground">
              {role ? "Edit role" : "New role"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {role?.isSystem
                ? "System role · the slug is locked but you can edit label, description, and permissions."
                : "Pick the permissions you want this role to grant."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground transition hover:bg-surface-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-6">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">Role name</span>
            <input
              type="text"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="e.g. Box Office Lead"
              className="ego-input"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">
              Description <span className="font-normal">(optional)</span>
            </span>
            <input
              type="text"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What this role can do"
              className="ego-input"
            />
          </label>

          <div>
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">Permissions</span>
            <div className="flex flex-wrap gap-1.5">
              {ALL_PERMISSIONS.map((permission) => {
                const isOn = permissions.includes(permission);
                return (
                  <button
                    key={permission}
                    type="button"
                    onClick={() => togglePermission(permission)}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${
                      isOn
                        ? "border-[var(--pp-gold)] bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]"
                        : "border-line bg-surface text-muted-foreground hover:bg-surface-muted"
                    }`}
                  >
                    {PERMISSION_LABELS[permission]}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="border-t border-line bg-surface-muted px-6 py-4">
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="ego-btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSubmitting}
              className="ego-btn-primary flex-1"
            >
              {isSubmitting ? "Saving..." : role ? "Save changes" : "Create role"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InviteAdminDialog({
  roles,
  events,
  isOrganizer,
  existingInviteUrl,
  onClose,
  onCreated,
}: {
  roles: AdminRoleDefinition[];
  events: EventItem[];
  isOrganizer: boolean;
  existingInviteUrl: string | null;
  onClose: () => void;
  onCreated: (invite: AdminInvite, inviteToken: string) => void;
}) {
  const { token } = useAdminAuth();
  const { showToast } = useToast();
  const [email, setEmail] = useState("");
  const availableRoles = isOrganizer
    ? roles.filter((option) => ORGANIZER_COLLABORATOR_ROLES.has(option.name))
    : roles;
  const defaultRole =
    (isOrganizer
      ? availableRoles.find((option) => option.name === "scanner")
      : availableRoles.find((option) => option.name === "manager")) ?? availableRoles[0];
  const [role, setRole] = useState<string>(defaultRole?.name ?? "manager");
  const [permissions, setPermissions] = useState<AdminPermission[]>([]);
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>(events[0]?.id ? [events[0].id] : []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [issuedUrl, setIssuedUrl] = useState<string | null>(existingInviteUrl);
  const [copied, setCopied] = useState(false);

  const togglePermission = (permission: AdminPermission) => {
    setPermissions((current) =>
      current.includes(permission)
        ? current.filter((entry) => entry !== permission)
        : [...current, permission]
    );
  };

  const handleSubmit = async () => {
    if (!token) return;
    if (!email.trim()) {
      showToast("Email is required", "error");
      return;
    }
    if (isOrganizer && selectedEventIds.length === 0) {
      showToast("Select a screening for this collaborator", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const { invite, token: inviteToken } = await adminApi.createInvite(token, {
        email: email.trim(),
        role: role as AdminRole,
        permissions: isOrganizer || role === "super-admin" ? [] : permissions,
        eventIds: isOrganizer ? selectedEventIds : undefined,
      });
      const url = buildInviteUrl(inviteToken);
      setIssuedUrl(url);
      onCreated(invite, inviteToken);
      showToast("Invite ready · share the link", "success");
    } catch (requestError) {
      showToast(
        requestError instanceof Error ? requestError.message : "Could not create invite",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyUrl = async () => {
    if (!issuedUrl) return;
    try {
      await navigator.clipboard.writeText(issuedUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
      showToast("Link copied", "success");
    } catch {
      showToast("Could not copy. Please copy manually.", "error");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch bg-black/60 p-3 backdrop-blur-md dark:bg-black/75 sm:p-6">
      <div className="mx-auto flex w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-surface shadow-2xl">
        <div className="flex items-start justify-between border-b border-line px-6 py-5">
          <div>
            <span className="ego-badge ego-badge-brand">Team</span>
            <h2 className="mt-2 text-xl font-bold tracking-tight text-foreground">
              {isOrganizer ? "Invite collaborator" : "Invite admin"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isOrganizer
                ? "They'll get access only to the screening you assign."
                : "They'll get a link to set their own password."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground transition hover:bg-surface-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-6">
          {issuedUrl ? (
            <div className="space-y-3">
              <div className="rounded-xl border border-[rgba(4,120,87,0.18)] bg-[#ecfdf5] dark:bg-[rgba(16,185,129,0.14)] px-4 py-3 text-sm font-semibold text-[#047857]">
                Invite created. Share this link with the new admin.
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-line bg-surface-muted p-2">
                <Mail size={14} className="ml-1 shrink-0 text-muted-foreground" />
                <code className="flex-1 truncate font-mono text-xs text-foreground">{issuedUrl}</code>
                <button type="button" onClick={copyUrl} className="ego-btn-secondary !py-1.5 !px-3 text-xs">
                  {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                The invite expires in 7 days. They can set their name and password when they open
                the link.
              </p>
            </div>
          ) : (
            <>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-muted-foreground">Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@example.com"
                  className="ego-input"
                />
              </label>

              <div>
                <span className="mb-1 block text-xs font-semibold text-muted-foreground">
                  {isOrganizer ? "Access type" : "Role"}
                </span>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {availableRoles.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        setRole(option.name);
                        if (isOrganizer) setPermissions([]);
                      }}
                      className={`rounded-xl border px-3 py-2 text-left text-sm font-semibold transition ${
                        role === option.name
                          ? "border-[var(--pp-gold)] bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]"
                          : "border-line bg-surface text-muted-foreground hover:bg-surface-muted"
                      }`}
                    >
                      <div>{option.label}</div>
                      {option.description && (
                        <div className="mt-0.5 text-[10px] font-normal text-[#9aa3b2]">
                          {option.description}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {isOrganizer && (
                <div>
                  <span className="mb-1 block text-xs font-semibold text-muted-foreground">Screening</span>
                  {events.length === 0 ? (
                    <div className="rounded-xl border border-line bg-surface-muted px-4 py-3 text-sm text-muted-foreground">
                      Create a screening before inviting collaborators.
                    </div>
                  ) : (
                    <div className="max-h-56 space-y-2 overflow-y-auto rounded-xl border border-line bg-surface p-2">
                      {events.map((event) => {
                        const selected = selectedEventIds.includes(event.id);
                        return (
                          <button
                            key={event.id}
                            type="button"
                            onClick={() => setSelectedEventIds([event.id])}
                            className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition ${
                              selected
                                ? "border-[var(--pp-gold)] bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)]"
                                : "border-transparent hover:bg-surface-muted"
                            }`}
                          >
                            <img src={event.image} alt="" className="h-10 w-12 rounded-md object-cover" />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-semibold text-foreground">{event.title}</div>
                              <div className="truncate text-xs text-muted-foreground">
                                {event.venue}, {event.city}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {!isOrganizer && role !== "super-admin" && (
                <div>
                  <span className="mb-1 block text-xs font-semibold text-muted-foreground">
                    Extra permissions <span className="font-normal">(on top of role)</span>
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {ALL_PERMISSIONS.map((permission) => {
                      const isOn = permissions.includes(permission);
                      return (
                        <button
                          key={permission}
                          type="button"
                          onClick={() => togglePermission(permission)}
                          className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${
                            isOn
                              ? "border-[var(--pp-gold)] bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]"
                              : "border-line bg-surface text-muted-foreground hover:bg-surface-muted"
                          }`}
                        >
                          {PERMISSION_LABELS[permission]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="border-t border-line bg-surface-muted px-6 py-4">
          {issuedUrl ? (
            <button type="button" onClick={onClose} className="ego-btn-primary w-full">
              Done
            </button>
          ) : (
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="ego-btn-secondary flex-1">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || (isOrganizer && selectedEventIds.length === 0)}
                className="ego-btn-primary flex-1"
              >
                {isSubmitting ? "Creating..." : "Create invite"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
